// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';

import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import * as WidgetsThingsStructs from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';

import { LazyLoadingAnimation } from '@Primitive/Lazy';

import { PortKeys, DemandPlanogramsEditorData } from "./DemandPlanogramsEditorStructs";
// ------------------------------------------------------------------------------------------------------
const DemandPlanogramsEditor = React.lazy(() => import("./DemandPlanogramsEditorAsync"));
const DemandPlanogramsEditorSettings = React.lazy(() => import("./DemandPlanogramsEditorAsync").then(module => ({ default: module.DemandPlanogramsEditorSettings })));
// =================================================================================================
export class DemandPlanogramsEditorWidget extends IWidgetThingsClass<DemandPlanogramsEditorData> {
    Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <DemandPlanogramsEditor />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <DemandPlanogramsEditorSettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: DemandPlanogramsEditorData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort({
            Name: 'Data',
            PortKey: PortKeys.Data,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            ConfFlags: Plegma.ePortConf.None,
            Description: '',
            State: ''
        }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.Data, data)));

        ports.push(new WebThingPort({
            Name: 'onButtonClick',
            PortKey: PortKeys.onButtonClick,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            ConfFlags: Plegma.ePortConf.None,
            Description: '',
            State: ''
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: 'onSaveReq',
            PortKey: PortKeys.onSaveReq,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            ConfFlags: Plegma.ePortConf.None,
            Description: '',
            State: ''
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: 'onSaveSettingsReq',
            PortKey: PortKeys.onSaveSettingsReq,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            ConfFlags: Plegma.ePortConf.None,
            Description: '',
            State: ''
        }, WebThingPortType.Data));

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this.createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as DemandPlanogramsEditorData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): DemandPlanogramsEditorData {
        return {
            // TODO: Set the default values for the widget
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return DemandPlanogramsEditorWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'Demand Planogram Editor',
            WidgetTitle: '',
            Key: 'DemandPlanogramsEditorWidget',
            Type: 'DemandPlanogramsEditorWidget',
            Data: DemandPlanogramsEditorWidget.GetDefaultWidgetDataStatic(),
            Description: 'DemandPlanogramsEditorWidget Description',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: DemandPlanogramsEditorData): DemandPlanogramsEditorData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}